import type { TonAsset, TonSendTransactionRequest } from '../api/dto/offers.dto.js';
import { TelegramAPISettings } from '../telegram_api_settings.js';

/**
 * TON Connect for the web build. The server builds every transaction; this
 * plugin only connects a wallet, hands the request to it and returns what the
 * wallet says. "Sent" is never "paid": the server watches the chain and the
 * caller polls the order until it is granted.
 */

export type TonPaymentError =
  | 'cancelled_by_user'
  | 'tx_expired'
  | 'wrong_network'
  | 'wallet_not_connected'
  | 'wallet_unavailable'
  | 'unknown';

export class TonConnectFailure extends Error
{
  constructor( public readonly reason: TonPaymentError, message?: string )
  {
    super( message ?? reason );
    this.name = 'TonConnectFailure';
  }
}

/** The purchase left the sheet while the chain was still confirming: not a failure, the background tracker finishes it. */
export class TonPaymentPending extends Error
{
  constructor( public readonly order_id?: string )
  {
    super( 'ton_payment_processing' );
    this.name = 'TonPaymentPending';
  }
}

// The one wallet round-trip in flight (connect or sign). Cancelling from our
// own sheet rejects it and closes the TON Connect modal; a wallet that signs
// anyway is harmless, the server credits the order and the tracker picks it up.
let pending_reject: ( ( error: TonConnectFailure ) => void ) | null = null;

function cancellable<T>( work: Promise<T> ): Promise<T>
{
  return new Promise<T>( ( resolve, reject ) =>
  {
    pending_reject = reject;
    work.then( resolve, reject ).finally( () => { if ( pending_reject === reject ) pending_reject = null; } );
  } );
}

interface TonPrices
{
  ton?: number;
  usdt?: number;
  usd?: number;
}

// productId -> catalog prices, filled from the offers list. The IAP facade
// reads it to draw the button price without knowing about offers.
const prices_by_product = new Map<string, TonPrices>();

let ui_instance: any = null;
let ui_loading: Promise<any> | null = null;

function isNativeApp(): boolean
{
  const platform = ( window as any )?.Capacitor?.getPlatform?.();
  return platform === 'android' || platform === 'ios';
}

/** Inside a Telegram Mini App the wallet must bring the player back to the app, not to a browser. */
async function twaReturnUrl(): Promise<string | undefined>
{
  const override = ( window as any )?.TON_CONNECT_TWA_RETURN_URL;
  if ( typeof override === 'string' && override ) return override;
  if ( !TelegramAPISettings.isTelegramEnvironment() ) return undefined;
  try
  {
    const { InviteService } = await import( '../friends/invite_service.js' );
    const link = ( InviteService.get() ?? await InviteService.refresh() )?.link;
    return typeof link === 'string' && link ? link.split( '?' )[0] : undefined;
  }
  catch
  {
    return undefined;
  }
}

function manifestUrl(): string
{
  const override = ( window as any )?.TON_CONNECT_MANIFEST_URL;
  if ( typeof override === 'string' && override ) return override;
  return `${location.origin}/tonconnect-manifest.json`;
}

function formatUnits( value: number, decimals: number ): string
{
  const text = value.toFixed( decimals );
  return text.replace( /(\.\d*?[1-9])0+$/, '$1' ).replace( /\.0+$/, `.${'0'.repeat( Math.min( decimals, 2 ) )}` );
}

export const TonPayments = {
  /** Web and Telegram builds, never native store builds; the server decides per channel whether prices are sent at all. */
  isAvailable(): boolean
  {
    if ( isNativeApp() ) return false;
    return prices_by_product.size > 0;
  },

  /** Called before a fresh offers list is remembered, so a product that lost its crypto price stops offering it. */
  clearPrices(): void
  {
    prices_by_product.clear();
  },

  rememberPrices( product_id: string, prices: TonPrices ): void
  {
    if ( !product_id ) return;
    const clean: TonPrices = {};
    if ( typeof prices.ton === 'number' && prices.ton > 0 ) clean.ton = prices.ton;
    if ( typeof prices.usdt === 'number' && prices.usdt > 0 ) clean.usdt = prices.usdt;
    if ( typeof prices.usd === 'number' && prices.usd >= 0 ) clean.usd = prices.usd;
    if ( clean.ton === undefined && clean.usdt === undefined ) return;
    prices_by_product.set( product_id, clean );
  },

  getPrices( product_id: string ): TonPrices | null
  {
    return prices_by_product.get( product_id ) ?? null;
  },

  /** The asset drawn on the card: USDT when sold for it (stable, equals the USD price), else TON. */
  defaultAsset( product_id: string ): TonAsset | null
  {
    const prices = prices_by_product.get( product_id );
    if ( !prices ) return null;
    return prices.usdt !== undefined ? 'USDT' : 'TON';
  },

  formatAsset( asset: TonAsset, amount: number ): string
  {
    return asset === 'TON' ? `${formatUnits( amount, 3 )} TON` : `${formatUnits( amount, 2 )} USDT`;
  },

  /** "4.99 USDT" / "1.734 TON ≈ $4.99" for a product. */
  formatPrice( product_id: string ): string | null
  {
    const prices = prices_by_product.get( product_id );
    const asset = this.defaultAsset( product_id );
    if ( !prices || !asset ) return null;
    const amount = asset === 'TON' ? prices.ton! : prices.usdt!;
    const base = this.formatAsset( asset, amount );
    return asset === 'TON' && prices.usd !== undefined ? `${base} ≈ $${prices.usd.toFixed( 2 )}` : base;
  },

  async ui(): Promise<any>
  {
    if ( ui_instance ) return ui_instance;
    if ( !ui_loading )
    {
      ui_loading = Promise.all( [ import( '@tonconnect/ui' ), twaReturnUrl() ] ).then( ( [ mod, twa_return_url ] ) =>
      {
        const TonConnectUI = ( mod as any ).TonConnectUI;
        ui_instance = new TonConnectUI( {
          manifestUrl: manifestUrl(),
          uiPreferences: { theme: 'DARK' },
          actionsConfiguration: { returnStrategy: 'back', modals: [ 'before', 'error' ], notifications: [ 'error' ], ...( twa_return_url ? { twaReturnUrl: twa_return_url } : {} ) },
        } );
        return ui_instance;
      } ).catch( ( e ) =>
      {
        ui_loading = null;
        throw new TonConnectFailure( 'wallet_unavailable', e instanceof Error ? e.message : String( e ) );
      } );
    }
    return ui_loading;
  },

  /** Raw wallet address (0:hex) of the connected wallet, or null. */
  async connectedAddress(): Promise<string | null>
  {
    const ui = await this.ui();
    return ui.account?.address ?? null;
  },

  async connectedChain(): Promise<string | null>
  {
    const ui = await this.ui();
    return ui.account?.chain ?? null;
  },

  /** Opens the wallet picker when nothing is connected; resolves with the raw address. */
  async connect(): Promise<string>
  {
    const ui = await this.ui();
    if ( ui.connected && ui.account?.address ) return ui.account.address;
    try
    {
      if ( typeof ui.connectWallet === 'function' )
      {
        await cancellable( ui.connectWallet() );
      }
      else
      {
        await ui.openModal();
        await cancellable( new Promise<void>( ( resolve, reject ) =>
        {
          const unsubscribe = ui.onStatusChange( ( wallet: unknown ) =>
          {
            if ( wallet ) { unsubscribe(); resolve(); }
          }, ( error: unknown ) => { unsubscribe(); reject( error ); } );
          ui.onModalStateChange?.( ( state: any ) =>
          {
            if ( state?.status === 'closed' && state?.closeReason === 'action-cancelled' ) { unsubscribe(); reject( new TonConnectFailure( 'cancelled_by_user' ) ); }
          } );
        } ) );
      }
    }
    catch ( e )
    {
      throw classify( e );
    }
    const address = ui.account?.address;
    if ( !address ) throw new TonConnectFailure( 'wallet_not_connected' );
    return address;
  },

  /** Our sheet's Cancel during connect/sign: rejects the round-trip and closes the TON Connect modal. */
  cancelPending(): void
  {
    const reject = pending_reject;
    pending_reject = null;
    if ( ui_instance )
    {
      try { ui_instance.closeModal?.( 'action-cancelled' ); } catch { /* no modal open */ }
      try { ui_instance.closeSingleWalletModal?.( 'action-cancelled' ); } catch { /* no modal open */ }
    }
    reject?.( new TonConnectFailure( 'cancelled_by_user', 'cancelled from the payment sheet' ) );
  },

  async disconnect(): Promise<void>
  {
    const ui = await this.ui();
    try { await ui.disconnect(); } catch { /* nothing to disconnect */ }
  },

  /** Connected right now; false until the library has loaded and restored the session. */
  isConnected(): boolean
  {
    return !!ui_instance?.connected && !!ui_instance?.account?.address;
  },

  /**
   * Reports the raw address on connect / session restore and null on disconnect; the current state
   * is reported as soon as the library is loaded. Returns an unsubscribe.
   */
  onStatusChange( cb: ( address: string | null ) => void ): () => void
  {
    let active = true;
    let unsubscribe: ( () => void ) | null = null;
    void this.ui().then( ( ui ) =>
    {
      if ( !active ) return;
      cb( ui.connected && ui.account?.address ? ui.account.address : null );
      unsubscribe = ui.onStatusChange( ( wallet: any ) => { if ( active ) cb( wallet?.account?.address ?? null ); } );
    } ).catch( () => { if ( active ) cb( null ); } );
    return () => { active = false; unsubscribe?.(); };
  },

  /** `EQ…` / `UQ…` spelling of a raw address for display; the raw one when the library is unavailable. */
  async friendlyAddress( raw: string ): Promise<string>
  {
    try
    {
      const mod: any = await import( '@tonconnect/ui' );
      return mod.toUserFriendlyAddress( raw, ( await this.connectedChain() ) === '-3' );
    }
    catch
    {
      return raw;
    }
  },

  /**
   * Hands the server-built request to the wallet. Resolves with the signed
   * message BOC when the wallet reports it sent; that is a hint, not a payment.
   */
  async sendTransaction( request: TonSendTransactionRequest ): Promise<string>
  {
    const ui = await this.ui();
    if ( !ui.connected ) throw new TonConnectFailure( 'wallet_not_connected' );
    const chain = ui.account?.chain;
    if ( chain && chain !== request.network ) throw new TonConnectFailure( 'wrong_network', `wallet is on ${chain}, the order is for ${request.network}` );
    try
    {
      const result: any = await cancellable( ui.sendTransaction( request ) );
      return typeof result?.boc === 'string' ? result.boc : '';
    }
    catch ( e )
    {
      throw classify( e );
    }
  },
};

/**
 * Maps TON Connect errors to the reasons the purchase flow and analytics
 * understand. TonConnectError subclasses do not set `name` and their class
 * names are minified in production, so the SDK's own messages are matched:
 * "Wallet was not connected" (picker closed), "Transaction was not sent"
 * (confirmation modal closed), plus the wallet-side UserRejectsError.
 */
export function classify( error: unknown ): TonConnectFailure
{
  if ( error instanceof TonConnectFailure ) return error;
  const name = error && typeof error === 'object' ? String( ( error as any ).name ?? '' ) : '';
  const message = error instanceof Error ? error.message : String( error ?? '' );
  const lower = `${name} ${message}`.toLowerCase();
  if ( name === 'UserRejectsError' || lower.includes( 'user reject' ) || lower.includes( 'cancel' )
    || lower.includes( 'was not connected' ) || lower.includes( 'was not sent' ) || lower.includes( 'user declined' ) ) return new TonConnectFailure( 'cancelled_by_user', message );
  if ( name === 'WalletNotConnectedError' ) return new TonConnectFailure( 'wallet_not_connected', message );
  if ( name === 'WalletWrongNetworkError' ) return new TonConnectFailure( 'wrong_network', message );
  if ( name === 'BadRequestError' || lower.includes( 'expired' ) || lower.includes( 'valid_until' ) || lower.includes( 'validuntil' ) ) return new TonConnectFailure( 'tx_expired', message );
  if ( name === 'FetchWalletsError' || name === 'WalletNotInjectedError' || lower.includes( 'not injected' ) || lower.includes( 'fetch wallets' ) || lower.includes( 'wallets list' ) ) return new TonConnectFailure( 'wallet_unavailable', message );
  return new TonConnectFailure( 'unknown', message );
}
